"use client";

import Link from "next/link";
import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";

import { TextField } from "@/components/shared/text-field";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup } from "@/components/ui/field";
import { routes } from "@/config/routes";
import { requestPasswordResetAction } from "@/features/auth/actions";
import { FormAlert } from "@/features/auth/components/form-alert";
import { FormHeader } from "@/features/auth/components/form-header";
import { applyServerErrors } from "@/features/auth/form-errors";
import { forgotPasswordSchema, type ForgotPasswordInput } from "@/features/auth/schemas";

export function ForgotPasswordForm() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  async function handleSubmit(values: ForgotPasswordInput) {
    const result = await requestPasswordResetAction(values);
    if (result.ok) setSentTo(values.email);
    else applyServerErrors(form, ["email"], result.error, result.fieldErrors);
  }

  if (sentTo) {
    return (
      <div className="flex flex-col gap-6" role="status">
        <FormHeader
          title={t("forgot.sentTitle")}
          description={t("forgot.sentDescription", { email: sentTo })}
        />
        <Button variant="outline" asChild>
          <Link href={routes.login()}>{t("forgot.backToLogin")}</Link>
        </Button>
      </div>
    );
  }

  const { isSubmitting, errors } = form.formState;

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} noValidate className="flex flex-col gap-6">
      <FieldGroup>
        <FormHeader title={t("forgot.title")} description={t("forgot.description")} />
        <FormAlert message={errors.root?.message} />

        <TextField
          control={form.control}
          name="email"
          label={t("email")}
          type="email"
          autoComplete="email"
        />

        <Field>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? tc("sending") : t("forgot.submit")}
          </Button>
          <FieldDescription className="text-center">
            {t("forgot.rememberedIt")}{" "}
            <Link href={routes.login()} className="underline underline-offset-4">
              {t("forgot.login")}
            </Link>
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
}
